import { AppUtil, DateWrapper, DateUtil } from 'kuaidong-lib'

import {
  Clazz,
  IPagination,
  IExamRecord,
  IExamRecordEntry,
  IQueryPagination,
  IExamRecordDetails,
  ExamRecordItemValue,
  IEditExamRecordInfo,
  ISEaxmProjectOverview,
  IExamRecordReportRate,
  IExamRecordParseTitles,
  IExamRecordReportStudent,
  IExamRecordReportScoreRate,
  IExamRecordExcelInfo,
  IExamRecordUniversityDetails,
} from 'kuaidong-model'

type Rate = IExamRecordReportRate

type ScoreRate = IExamRecordReportScoreRate

function adjustEndDate(date: string) {
  return new DateWrapper(DateUtil.addDay(new DateWrapper(date || new Date()).date, 7)).showDateStr
}

export class AExamRecord {
  public static async all(
    query?: IQueryPagination,
    filter?: {
      start_time: string
      end_time: string
      status: string
      name: string
    }
  ): Promise<IPagination<IExamRecord>> {
    return AppUtil.httpGET('/body_exam/excel_data/list', { ...query, ...filter }).then((res) => ({
      ...res,
      items: res.items.map((item: any) => ({
        ...item,
        exam_date: item.exam_date || new Date(),
        end_date: item.end_date || adjustEndDate(item.exam_date),
      })),
    }))
  }

  public static async getImportTemplateInfo(examRecordId: string): Promise<IExamRecordExcelInfo> {
    return AppUtil.httpGET('/body_exam/excel_data/report_file', {
      upload_score_id: examRecordId,
    })
  }

  public static async find(id: string): Promise<IExamRecordUniversityDetails> {
    return AppUtil.httpGET('/body_exam/excel_data', { id }).then((res) => ({
      ...res,
      exam_date: res.exam_date || new Date(),
      end_date: res.end_date || adjustEndDate(res.exam_date),
    }))
  }

  public static async softFind(id: string): Promise<IExamRecordDetails & { body_exam_class_vos: Clazz[] }> {
    return AppUtil.httpGET('/body_exam/excel_data_mobile', { id })
  }

  public static async save(data: IEditExamRecordInfo): Promise<IExamRecordDetails> {
    if (!data.year) {
      data.year = data.exam_date.getFullYear()
    }

    if (data.id) {
      return AppUtil.httpPUT('/body_exam/excel_data', data)
    } else {
      return AppUtil.httpPOST('/body_exam/excel_data', data)
    }
  }

  public static async merge(data: IEditExamRecordInfo) {
    return AppUtil.httpPOST('/body_exam/excel_data/merge_items', data)
  }

  public static async delete(id: string) {
    return AppUtil.httpDelete('/body_exam/excel_data', { id })
  }

  public static async upload(id: string, url: string, titleMap: { [index: string]: string }, onlyIdentifiers = false) {
    return AppUtil.httpPUT('/body_exam/excel_data', {
      id,
      excel_url: url,
      column_map: titleMap,
      short_title: onlyIdentifiers,
    })
  }

  public static async summary(
    id: string
  ): Promise<IExamRecordDetails & { item_stats_summary_list: ISEaxmProjectOverview[] }> {
    return AppUtil.httpGET('/cel/body_exam/stat/upload_score_info', { upload_score_id: id })
  }

  public static async download(
    id: string,
    url: string,
    data_type: 'score' | 'all',
    titleMap: { [index: string]: string },
    filename: string
  ) {
    return AppUtil.httpPOST('/body_exam/excel_data/make_report_file', {
      id,
      column_map: titleMap,
      data_type,
      excel_template_url: url,
      excel_template_filename: filename,
    })
  }

  public static async download2(upload_score_id: string, data_type: 'score' | 'all') {
    return AppUtil.httpGET('/body_exam/excel_data/download_body_exam_excel', { upload_score_id, data_type })
  }

  public static async parseTitles(url: string, onlyIdentifiers = false): Promise<IExamRecordParseTitles> {
    return AppUtil.httpPOST('/async/body_exam/excel_data/parse_titles', {
      excel_url: url,
      short_title: onlyIdentifiers,
    })
  }

  public static async saveStudent(student: { id: string; items: { [index: string]: ExamRecordItemValue } }) {
    return AppUtil.httpPUT('/body_exam/excel_data/student_data', student)
  }

  public static async createStudent(student: any) {
    return AppUtil.httpPOST('/body_exam/excel_data/student_data', student)
  }

  public static async deleteStudent(id: string) {
    return AppUtil.httpDelete('/body_exam/excel_data/student_data', { id })
  }

  public static async publish(id: string) {
    return AppUtil.httpPOST('/body_exam/excel_data/publish', { id })
  }

  public static async unpublish(id: string) {
    return AppUtil.httpDelete('/body_exam/excel_data/publish', { id })
  }

  public static async rateSchoolStat(body_exam_upload_score_id: string, reportId?: string): Promise<Rate> {
    return AppUtil.httpGET('/body_exam/body_exam_rate/total', { body_exam_upload_score_id, report_id: reportId })
  }

  public static async rateSchoolDistribute(
    body_exam_upload_score_id: string,
    reportId: string
  ): Promise<{ score_rate: Rate[] }> {
    return AppUtil.httpGET('/body_exam/body_exam_rate/excellent_pass_rate', {
      body_exam_upload_score_id,
      report_id: reportId,
    })
  }

  public static async rateSchoolLastDistribute(): Promise<{
    year: string
    score_type: string
    body_exam_total: { average: string }
    body_exam_total_rate: { score_rate: Rate[] }
    body_exam_excellent_pass_rate: { score_rate: Rate[] }
  }> {
    return AppUtil.httpGET('/body_exam/body_exam_rate/school_stat')
  }

  public static async rateSchoolDistributeDetail(
    body_exam_upload_score_id: string,
    gender: string,
    reportId: string
  ): Promise<{ score_rate_list: ScoreRate[] }> {
    return AppUtil.httpGET('/body_exam/body_exam_rate/class_excellent_pass_rate', {
      body_exam_upload_score_id,
      gender,
      report_id: reportId,
    })
  }

  public static async rateSchoolRing(
    body_exam_upload_score_id: string,
    gender: string,
    reportId: string
  ): Promise<{ score_rate: Rate[] }> {
    return AppUtil.httpGET('/body_exam/body_exam_rate/total_rate', {
      body_exam_upload_score_id,
      gender,
      report_id: reportId,
    })
  }

  public static async rateSchoolJobs(
    body_exam_upload_score_id: string,
    gender: string,
    reportId?: string
  ): Promise<{ score_rate: Rate[] }> {
    return AppUtil.httpGET('/body_exam/body_exam_rate/score_rate', {
      body_exam_upload_score_id,
      gender,
      report_id: reportId,
    })
  }

  public static async rateSchoolGrades(
    body_exam_upload_score_id: string,
    gender: string,
    reportId: string
  ): Promise<{ score_rate: Rate[] }> {
    return AppUtil.httpGET('/body_exam/body_exam_rate/grade_rate', {
      body_exam_upload_score_id,
      gender,
      report_id: reportId,
    })
  }

  public static async rateGradeClasses(
    body_exam_upload_score_id: string,
    grade_no: number,
    gender: string,
    reportId: string
  ): Promise<{ score_rate: Rate[] }> {
    return AppUtil.httpGET('/body_exam/body_exam_rate/grade_class_score_rate', {
      body_exam_upload_score_id,
      grade_no,
      gender,
      report_id: reportId,
    })
  }

  public static async rateGradeJobs(
    body_exam_upload_score_id: string,
    grade_no: number,
    gender: string,
    reportId: string
  ): Promise<{ score_rate: Rate[] }> {
    return AppUtil.httpGET('/body_exam/body_exam_rate/grade_score_rate', {
      body_exam_upload_score_id,
      grade_no,
      gender,
      report_id: reportId,
    })
  }

  public static async rateJobGrades(
    body_exam_upload_score_id: string,
    item_key: string,
    gender: string,
    reportId: string
  ): Promise<{ score_rate: Rate[] }> {
    return AppUtil.httpGET('/body_exam/body_exam_rate/score_grade_rate', {
      body_exam_upload_score_id,
      item_key,
      gender,
      report_id: reportId,
    })
  }

  public static async rateClassStat(
    body_exam_upload_score_id: string,
    class_no: string,
    reportId: string
  ): Promise<Rate> {
    return AppUtil.httpGET('/body_exam/body_exam_rate/class_total_rate', {
      body_exam_upload_score_id,
      class_no,
      report_id: reportId,
    })
  }

  public static async rateClassRing(
    body_exam_upload_score_id: string,
    class_no: string,
    gender: string,
    reportId: string
  ): Promise<{ score_rate: Rate[] }> {
    return AppUtil.httpGET('/body_exam/body_exam_rate/class_score_rate', {
      body_exam_upload_score_id,
      class_no,
      gender,
      report_id: reportId,
    })
  }

  public static async rateClassJobs(
    body_exam_upload_score_id: string,
    class_no: string,
    gender: string,
    reportId: string
  ): Promise<{ score_rate: Rate[] }> {
    return AppUtil.httpGET('/body_exam/body_exam_rate/class_item_rate', {
      body_exam_upload_score_id,
      class_no,
      gender,
      report_id: reportId,
    })
  }

  public static async rateClassStudents(
    body_exam_upload_score_id: string,
    class_no: string,
    reportId: string
  ): Promise<{ item_score_list: IExamRecordReportStudent[] }> {
    return AppUtil.httpGET('/body_exam/body_exam_rate/students_score_rate', {
      body_exam_upload_score_id,
      class_no,
      report_id: reportId,
    })
  }
  public static async rateClassSummary(
    body_exam_upload_score_id: string,
    class_no: string,
    gender: string,
    reportId: string
  ): Promise<{ class_score_rate: Rate }> {
    return AppUtil.httpGET('/body_exam/body_exam_rate/class_total_score_rate', {
      body_exam_upload_score_id,
      class_no,
      gender,
      report_id: reportId,
    })
  }
  public static async rateClassJobSummary(
    body_exam_upload_score_id: string,
    class_no: string,
    item_key: string,
    gender: string,
    reportId: string
  ) {
    return AppUtil.httpGET('/body_exam/body_exam_rate/class_item_single_score_rate', {
      body_exam_upload_score_id,
      class_no,
      item_key,
      gender,
      report_id: reportId,
    })
  }

  public static async syncStudentImport(upload_score_id: string) {
    return AppUtil.httpPUT('/body_exam/excel_data/sync_student_import', {
      upload_score_id,
    })
  }

  public static async schoolAverage(upload_score_id: string, gender: string, reportId: string): Promise<Rate[]> {
    return AppUtil.httpGET('/body_exam/body_exam_rate/school_student_average', {
      upload_score_id,
      gender,
      report_id: reportId,
    })
  }

  public static async syncStudentsFromExistedClazzes(id: string, clazzIds: number[], reportId: string) {
    return AppUtil.httpPOST('/body_exam/excel_data/import_from_class', {
      upload_score_id: id,
      class_ids: clazzIds,
      report_id: reportId,
    })
  }

  public static async adjust(form: {
    upload_score_id: string
    // delta_avg_score: number
    target: number
    column_name: string
    metric: string
  }) {
    return AppUtil.httpPOST('/body_exam/excel_data/flexible_adjust', form)
  }

  public static async getAdjustVal(form: {
    upload_score_id: string
    target: number // target不会用到
    column_name: string
    metric: string
  }): Promise<number> {
    const resp = await AppUtil.httpGET('/body_exam/excel_data/flexible_adjust/val', {
      upload_score_id: form.upload_score_id,
      column_name: form.column_name,
      metric: form.metric,
    })
    return parseFloat(`${resp}`)
  }

  public static async adjustSupportedEntries(): Promise<IExamRecordEntry[]> {
    return AppUtil.httpGET('/body_exam/excel_data/flexible_adjust')
  }
}
